/**
 * MobileDocumentsScreen — RC7 · M6 · First-class mobile Documents surface.
 *
 * Renders the canonical safe-descriptor catalogue from
 * `GET /api/mobile/documents/catalog` and coordinates C3 reveal via the
 * one biometric gate (`c3_biometric.js`) through the DocumentAssetStore.
 *
 * NON-NEGOTIABLE (PO decisions ratified 2026-02-17 · M6):
 *   · Descriptor-only. Never prefetches document bytes.
 *   · C3 items open a lock UI. Bytes are fetched ONLINE only after a
 *     fresh biometric event. Refuses offline C3 reveal.
 *   · Reveal auto-hides after 30 s; also cleared on route change,
 *     voyage switch, logout, and app backgrounding.
 *   · No sensitive value persists in localStorage / sessionStorage /
 *     URL / query / analytics / console.
 *
 * The App-level route resolver wraps this element in the standard mobile
 * chrome (top strip · bottom tab bar · Safety FAB). This component
 * renders the surface content only — no Shell wrapper here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FileText, Lock, ShieldAlert, Eye, EyeOff, Loader2, WifiOff,
  Plane, Utensils, MapPin, Building2, Car, Anchor, ScrollText,
  Landmark, HeartPulse, PhoneCall, IdCard, Plus, X, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { EMAIL_DOCUMENT_ACCEPT } from "@/lib/uploadFormats";
import { useDocumentOriginal, DocumentOriginalFrame } from "@/components/documents/DocumentOriginal";
import {
  DOCUMENT_CATEGORIES, UNLINKED_SECTION_NOTE, UNLINKED_SECTION_TITLE,
  documentDisplayName, groupDocumentsByCategory,
} from "@/lib/documentCategories";
import { PassportUploadNotice } from "@/components/documents/PassportUploadNotice";
import { useCruise } from "@/context/CruiseContext";
import { useAuth } from "@/context/AuthContext";
import {
  loadCatalogue,
  readCatalogue,
  revealSensitiveDocument,
  subscribeRevealClear,
  clearActiveReveal,
  installRevealClearHooks,
  DOCUMENT_ASSET_STORE_REVEAL_TIMEOUT_MS,
} from "@/mobile/offline/documentAssetStore";
import { inspectBiometric } from "@/mobile/offline/c3_biometric";

// Local connectivity mirror — the M4 offlineAwareFetcher has a
// pre-existing compile issue (pack.js imports), unrelated to M6. M6 is
// descriptor-only so we only need a plain online/offline flag.
function isOnline() {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F2244";
const GOLD = "#C28E42";
const RED = "#B91C1C";

// Map descriptor type → icon. Purely decorative — never used to invent
// document data. Falls back to generic FileText.
const TYPE_ICON = {
  passport:            IdCard,
  visa:                ScrollText,
  insurance:           HeartPulse,
  health_declaration:  HeartPulse,
  emergency_contact:   PhoneCall,
  boarding_pass:       Plane,
  "boarding-pass":     Plane,
  "flight":            Plane,
  "hotel":             Building2,
  "excursion":         MapPin,
  "rental-car":        Car,
  "parking":           Car,
  "transportation":    Car,
  "dining":            Utensils,
  "cruise":            Anchor,
  "passport-copy":     Landmark,
};

function iconFor(descriptor) {
  const t = descriptor.document_type;
  return TYPE_ICON[t] || FileText;
}

function ReasonMessage({ reason }) {
  // Traveller-friendly copy for every failure path. Never HTTP codes
  // and never SQLite / API terminology per PO §11.
  const MAP = {
    biometric_unavailable:
      "Face ID / fingerprint isn't available on this device — sensitive documents can be viewed on your mobile device.",
    biometric_cancelled:
      "Reveal cancelled.",
    biometric_failed:
      "Couldn't verify your identity. Please try again.",
    offline:
      "You're offline. Sensitive documents open only when you're back online.",
    not_found:
      "This document is no longer available on this voyage.",
    c3_forbidden:
      "You don't have permission to view this document.",
    c3_class_missing:
      "This document doesn't require identity unlock.",
    server_error:
      "Something went wrong opening this document. Please try again in a moment.",
  };
  const label = MAP[reason] || "We couldn't open that document.";
  return (
    <div
      data-testid="cc-m6-reveal-error"
      className="mt-2 text-xs px-2.5 py-1.5 rounded-md"
      style={{ color: RED, background: "#FEF2F2", border: "1px solid #FECACA" }}
    >
      {label}
    </div>
  );
}


function ClassChip({ classification }) {
  const label = classification;
  const bg =
    classification === "C3" ? "#FEF3F2"
      : classification === "C2" ? "#FFF7ED"
        : "#F3F4F6";
  const fg =
    classification === "C3" ? "#B42318"
      : classification === "C2" ? "#B45309"
        : "#374151";
  return (
    <span
      data-testid={`cc-m6-class-chip-${classification}`}
      className="inline-flex items-center gap-1 text-[10px] tracking-[0.06em] uppercase font-semibold px-2 py-0.5 rounded-full"
      style={{ background: bg, color: fg }}
    >
      {classification === "C3" && <Lock className="w-3 h-3" />}
      {label} · {classification === "C3" ? "Identity" : classification === "C2" ? "Personal" : "Public"}
    </span>
  );
}


// Non-identity voyage-archive files open through the authorised archive download.
const openableArchiveDoc = (d) => d.source === "cruise_documents" && d.classification !== "C3";

function DocumentRow({ descriptor, online, onReveal, onOpen }) {
  const Icon = iconFor(descriptor);
  const isC3 = descriptor.classification === "C3";
  const canReveal = isC3 && online;
  return (
    <li
      data-testid="cc-m6-doc-row"
      data-doctype={descriptor.document_type}
      data-classification={descriptor.classification}
      className="bg-white rounded-xl border border-slate-200/70 p-4 flex items-start gap-3"
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: "#F1F5F9", color: NAVY }}
      >
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p
            data-testid="cc-m6-doc-name"
            className="text-sm font-semibold truncate"
            style={{ color: NAVY }}
          >
            {descriptor.display_name}
          </p>
          <ClassChip classification={descriptor.classification} />
        </div>
        <div className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          {descriptor.masked && (
            <span data-testid="cc-m6-doc-masked" className="font-mono">
              {descriptor.masked}
            </span>
          )}
          {descriptor.issuer && <span>{descriptor.issuer}</span>}
          {descriptor.country && <span>· {descriptor.country}</span>}
          {descriptor.expires && (
            <span data-testid="cc-m6-doc-expires">Exp {descriptor.expires}</span>
          )}
        </div>
        {openableArchiveDoc(descriptor) && (
          <OpenButton
            onClick={() => onOpen({
              path: `/api/cruises/current/documents/${encodeURIComponent(descriptor.document_id)}/download`,
              name: descriptor.display_name,
            })}
          />
        )}
        {isC3 && (
          <div className="mt-2">
            {canReveal ? (
              <button
                type="button"
                data-testid="cc-m6-reveal-btn"
                onClick={() => onReveal(descriptor)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors active:bg-slate-100"
                style={{ background: "#F8FAFC", color: NAVY, border: `1px solid ${GOLD}` }}
              >
                <Lock className="w-3.5 h-3.5" />
                Unlock with Face ID / fingerprint
              </button>
            ) : (
              <div
                data-testid="cc-m6-c3-offline"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md"
                style={{ color: "#B45309", background: "#FFFBEB" }}
              >
                <WifiOff className="w-3.5 h-3.5" />
                Document available when you're online
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}


function OpenButton({ onClick }) {
  return (
    <button
      type="button"
      data-testid="cc-m6-open-btn"
      onClick={onClick}
      className="mt-2 inline-flex items-center gap-1 min-h-[36px] px-3 rounded-md text-xs font-semibold transition-colors active:bg-slate-100"
      style={{ background: "#F8FAFC", color: NAVY, border: "1px solid #CBD5E1" }}
    >
      Open <ChevronRight className="w-3.5 h-3.5" />
    </button>
  );
}

const EMPTY_SCOPE = { linked: [], unlinked: [] };

function CategorisedImported({ docs, scope, onOpen }) {
  const buckets = groupDocumentsByCategory(docs);
  return DOCUMENT_CATEGORIES.filter((c) => buckets[c.key].length > 0).map((c) => (
    <div key={c.key} data-testid={`cc-m6-${scope}-category-${c.key}`} className="mb-3">
      <p className="text-[11px] font-semibold text-slate-500 mb-1.5">{c.label} · {buckets[c.key].length}</p>
      <ul className="space-y-2">
        {buckets[c.key].map((d) => <ImportedRow key={d.id} doc={d} onOpen={onOpen} />)}
      </ul>
    </div>
  ));
}

function ImportedRow({ doc, onOpen }) {
  return (
    <li data-testid="cc-m6-imported-row" className="bg-white rounded-xl border border-slate-200/70 p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#F1F5F9", color: NAVY }}>
        <FileText className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <p data-testid="cc-m6-imported-name" className="text-sm font-semibold truncate" style={{ color: NAVY }}>
          {documentDisplayName(doc)}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          {(doc.document_type || "other").replace(/_/g, " ")} · {(doc.created_at || "").slice(0, 10)}
        </p>
        <OpenButton
          onClick={() => onOpen({
            path: `/api/documents/pipeline/${encodeURIComponent(doc.id)}/original`,
            name: documentDisplayName(doc),
          })}
        />
      </div>
    </li>
  );
}

function DocumentViewer({ path, name, onClose }) {
  const original = useDocumentOriginal(path, name);
  return (
    <div data-testid="cc-m6-viewer" className="fixed inset-0 z-50 flex flex-col bg-white cc-safe-top">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <p data-testid="cc-m6-viewer-title" className="text-sm font-semibold truncate" style={{ color: NAVY }}>{name}</p>
        <button
          type="button"
          onClick={onClose}
          data-testid="cc-m6-viewer-close"
          aria-label="Close document"
          className="w-11 h-11 inline-flex items-center justify-center rounded-full active:bg-slate-100"
          style={{ color: NAVY }}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 p-3 overflow-auto">
        <DocumentOriginalFrame original={original} className="w-full h-full min-h-[60vh] border-0" />
      </div>
    </div>
  );
}

function RevealPanel({ reveal, remainingMs, onDismiss }) {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return (
    <div
      data-testid="cc-m6-reveal-panel"
      role="dialog"
      aria-live="polite"
      className="mt-4 rounded-2xl border p-4"
      style={{ borderColor: GOLD, background: "#FFFDF5" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4" style={{ color: GOLD }} />
          <p className="text-sm font-semibold" style={{ color: NAVY }}>
            Sensitive content shown
          </p>
        </div>
        <button
          type="button"
          data-testid="cc-m6-reveal-dismiss"
          onClick={onDismiss}
          className="inline-flex items-center gap-1 text-xs font-semibold"
          style={{ color: NAVY }}
        >
          <EyeOff className="w-3.5 h-3.5" /> Hide
        </button>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Auto-hiding in {seconds}s · verify with Face ID again to reveal after hiding.
      </p>
      {reveal.contentType && reveal.contentType.startsWith("application/pdf") && (
        <embed
          data-testid="cc-m6-reveal-pdf"
          src={`data:application/pdf;base64,${reveal.bodyBase64}`}
          type="application/pdf"
          className="mt-3 w-full h-64 rounded-md border border-slate-200"
        />
      )}
      {reveal.contentType && reveal.contentType.startsWith("image/") && (
        <img
          data-testid="cc-m6-reveal-image"
          src={`data:${reveal.contentType};base64,${reveal.bodyBase64}`}
          alt="Sensitive document"
          className="mt-3 w-full rounded-md border border-slate-200"
        />
      )}
      {reveal.contentType === "application/json" && reveal.body && (
        <div
          data-testid="cc-m6-reveal-json"
          className="mt-3 text-sm rounded-md bg-white p-3 border border-slate-200 space-y-1"
        >
          {Object.entries(reveal.body)
            .filter(([k]) => !k.startsWith("_") && k !== "cruise_id" && k !== "traveler_id")
            .map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 text-xs">
                <span className="text-slate-500 uppercase tracking-wide">{k.replace(/_/g, " ")}</span>
                <span className="font-mono text-slate-900 text-right break-all">
                  {typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}


export default function MobileDocumentsScreen() {
  // CruiseContext exposes the active voyage under the `voyage` key.
  // Legacy call sites destructure as `cruise` and rely on other data
  // paths; here we read the canonical key directly.
  const { voyage } = useCruise();
  const userId = voyage?.owner_id || voyage?.owner_user_id || null;
  const voyageId = voyage?.id || null;
  const authUser = useAuth()?.user;
  // Results from requests started under another cruise/user (or after unmount) are dropped.
  const scopeKey = `${authUser?.id || authUser?.user_id || ""}|${voyageId || ""}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  useEffect(() => () => { scopeRef.current = null; }, []);

  const [catalogue, setCatalogue] = useState(() =>
    userId && voyageId ? readCatalogue({ userId, voyageId }) : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [reveal, setReveal] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [pending, setPending] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [failReason, setFailReason] = useState(null);
  const [biometric, setBiometric] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imported, setImported] = useState(EMPTY_SCOPE);
  const [importedError, setImportedError] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const loadImported = useCallback(async (key) => {
    const res = await fetch(`${API}/api/documents/pipeline/scoped?voyage_id=${encodeURIComponent(voyageId)}`, { credentials: "include" });
    if (!res.ok) throw new Error(String(res.status));
    const body = await res.json();
    if (scopeRef.current === key) {
      setImported({
        linked: Array.isArray(body?.linked) ? body.linked : [],
        unlinked: Array.isArray(body?.unlinked) ? body.unlinked : [],
      });
    }
  }, [voyageId]);

  // Same import pipeline as the Documents workspace (SHA-256 dedupe, classification,
  // provenance), tagged to this voyage. Email/document formats only — no camera/video.
  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const key = scopeRef.current;
    setUploading(true);
    let ok = 0;
    let dup = 0;
    const errors = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_module", "documents_workspace");
      fd.append("related_voyage_id", voyageId);
      fd.append("voyage_scope", "required");
      try {
        const res = await fetch(`${API}/api/documents/pipeline/upload`, {
          method: "POST", credentials: "include", body: fd,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) errors.push(`${file.name}: ${typeof body?.detail === "string" ? body.detail : `upload failed (${res.status})`}`);
        else if (body?.is_duplicate) dup += 1;
        else ok += 1;
      } catch {
        errors.push(`${file.name}: couldn't reach Cruise Command — check your connection.`);
      }
    }
    const sameScope = scopeRef.current === key;
    if (sameScope) setUploading(false);
    if (ok) toast.success(ok === 1 ? "1 document uploaded — classifying now." : `${ok} documents uploaded — classifying now.`);
    if (dup) toast.message(dup === 1 ? "1 file was already on record." : `${dup} files were already on record.`);
    if (errors.length) toast.error(errors.join("\n"));
    if (sameScope && (ok || dup)) {
      try { await loadImported(key); } catch { /* list keeps its last state */ }
    }
  };

  // Install window-level clear hooks once.
  useEffect(() => { installRevealClearHooks(); }, []);

  useEffect(() => {
    let cancelled = false;
    inspectBiometric().then((b) => { if (!cancelled) setBiometric(b); });
    return () => { cancelled = true; };
  }, []);

  // Load catalogue on scope change. On voyage switch we drop the
  // previous partition FIRST (defence-in-depth) then re-fetch.
  useEffect(() => {
    if (!userId || !voyageId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const c = await loadCatalogue({ userId, voyageId });
        if (!cancelled) setCatalogue(c);
      } catch (e) {
        if (!cancelled) setError(String(e?.response?.status || e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, voyageId]);

  useEffect(() => {
    setViewing(null);
    setUploading(false);
    setImported(EMPTY_SCOPE);
    setImportedError(false);
    if (!voyageId) return;
    const key = scopeKey;
    loadImported(key).catch(() => { if (scopeRef.current === key) setImportedError(true); });
  }, [scopeKey, voyageId, loadImported]);

  // Subscribe to the reveal-clear bus. Any signal (timeout · route
  // change · voyage switch · logout · backgrounding) drops the local
  // sensitive state.
  useEffect(() => {
    const off = subscribeRevealClear(() => {
      setReveal(null);
      setRemainingMs(0);
    });
    return off;
  }, []);

  // Connectivity mirror.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Auto-hide reveal after 30 s courtesy timer.
  useEffect(() => {
    if (!reveal) return undefined;
    setRemainingMs(reveal.expiresAt - Date.now());
    const int = setInterval(() => {
      const rem = reveal.expiresAt - Date.now();
      if (rem <= 0) {
        setReveal(null);
        clearActiveReveal("timeout");
      } else {
        setRemainingMs(rem);
      }
    }, 500);
    return () => clearInterval(int);
  }, [reveal]);

  const dismiss = useCallback(() => {
    setReveal(null);
    clearActiveReveal("dismiss");
  }, []);

  const doReveal = useCallback(async (descriptor) => {
    if (!userId || !voyageId) return;
    setFailReason(null);
    setPending(true);
    setPendingId(descriptor.document_id);
    try {
      const r = await revealSensitiveDocument({
        userId, voyageId, descriptor,
      });
      if (!r.ok) {
        setFailReason(r.reason || "server_error");
        setReveal(null);
      } else {
        setReveal(r);
        setFailReason(null);
      }
    } finally {
      setPending(false);
      setPendingId(null);
    }
  }, [userId, voyageId]);

  const grouped = useMemo(() => {
    const list = catalogue?.descriptors || [];
    return {
      C3: list.filter((d) => d.classification === "C3"),
      C2: list.filter((d) => d.classification === "C2"),
      C1: list.filter((d) => d.classification === "C1"),
    };
  }, [catalogue]);

  if (!userId || !voyageId) {
    return (
      <div
        data-testid="cc-mobile-documents-screen"
        className="pb-24 min-h-screen"
        style={{ background: "#F8FAFC" }}
      >
        <div
          data-testid="cc-m6-empty-no-voyage"
          className="p-6 text-center text-sm text-slate-500"
        >
          Select a voyage to view your documents.
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="cc-mobile-documents-screen"
      className="pb-24 min-h-screen"
      style={{ background: "#F8FAFC" }}
    >
      {/* Header */}
      <header className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: NAVY }}>
            Documents
          </h1>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="cc-m6-add-document"
            className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-full text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: NAVY }}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={EMAIL_DOCUMENT_ACCEPT}
            className="hidden"
            onChange={uploadFiles}
            data-testid="cc-m6-add-document-input"
          />
        </div>
        <p className="text-xs mt-1 text-slate-500">
          Passports and other identity documents unlock with Face ID or
          fingerprint on your mobile device.
        </p>
        <div className="mt-2">
          <PassportUploadNotice testId="cc-m6-passport-upload-notice" />
        </div>
        {!online && (
          <div
            data-testid="cc-m6-offline-banner"
            className="mt-3 inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md"
            style={{ color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A" }}
          >
            <WifiOff className="w-3.5 h-3.5" />
            You're offline · Showing saved information
          </div>
        )}
        {biometric && !biometric.is_available && (
          <div
            data-testid="cc-m6-biometric-unavailable-banner"
            className="mt-3 inline-flex items-start gap-2 text-xs px-2.5 py-1.5 rounded-md"
            style={{ color: NAVY, background: "#EEF2FF", border: "1px solid #C7D2FE" }}
          >
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5" />
            <span>
              Face ID / fingerprint isn't available in this view. Open the
              Cruise Command mobile app to reveal sensitive documents.
            </span>
          </div>
        )}
      </header>

      {loading && (
        <div className="p-6 flex items-center justify-center text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading documents…
        </div>
      )}

      {error && (
        <div data-testid="cc-m6-error" className="mx-4 mt-2 text-sm p-3 rounded-md" style={{ background: "#FEF2F2", color: RED }}>
          We couldn't load your documents. Please try again.
        </div>
      )}

      {catalogue && !loading && (
        <div className="px-4 space-y-6">
          {/* C3 Sensitive Identity */}
          {grouped.C3.length > 0 && (
            <section data-testid="cc-m6-section-c3">
              <h2 className="text-xs tracking-[0.08em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
                Identity Documents
              </h2>
              <ul className="space-y-2">
                {grouped.C3.map((d) => (
                  <React.Fragment key={d.document_id}>
                    <DocumentRow
                      descriptor={d}
                      online={online}
                      onReveal={doReveal}
                      onOpen={setViewing}
                    />
                    {pending && pendingId === d.document_id && (
                      <li className="text-xs text-slate-500 flex items-center gap-2 pl-2">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Verifying…
                      </li>
                    )}
                    {failReason && pendingId === null && reveal === null && (
                      // Only render the last failure once, near the last-attempted row.
                      // Guarded so it never accompanies a successful reveal.
                      d.document_id === grouped.C3[grouped.C3.length - 1].document_id && (
                        <li><ReasonMessage reason={failReason} /></li>
                      )
                    )}
                  </React.Fragment>
                ))}
              </ul>
              {reveal && <RevealPanel reveal={reveal} remainingMs={remainingMs} onDismiss={dismiss} />}
            </section>
          )}

          {/* C2 Personal */}
          {grouped.C2.length > 0 && (
            <section data-testid="cc-m6-section-c2">
              <h2 className="text-xs tracking-[0.08em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
                Travel Documents
              </h2>
              <ul className="space-y-2">
                {grouped.C2.map((d) => (
                  <DocumentRow key={d.document_id} descriptor={d} online={online} onReveal={doReveal} onOpen={setViewing} />
                ))}
              </ul>
            </section>
          )}

          {imported.linked.length > 0 && (
            <section data-testid="cc-m6-section-imported">
              <h2 className="text-xs tracking-[0.08em] uppercase font-semibold mb-2" style={{ color: NAVY }}>
                Imported Documents
              </h2>
              <CategorisedImported docs={imported.linked} scope="linked" onOpen={setViewing} />
            </section>
          )}

          {imported.unlinked.length > 0 && (
            <section data-testid="cc-m6-section-unlinked">
              <h2 className="text-xs tracking-[0.08em] uppercase font-semibold" style={{ color: NAVY }}>
                {UNLINKED_SECTION_TITLE}
              </h2>
              <p data-testid="cc-m6-unlinked-note" className="mt-1 mb-2 text-xs text-slate-500">{UNLINKED_SECTION_NOTE}</p>
              <CategorisedImported docs={imported.unlinked} scope="unlinked" onOpen={setViewing} />
            </section>
          )}

          {importedError && (
            <div data-testid="cc-m6-imported-error" className="text-sm p-3 rounded-md" style={{ background: "#FEF2F2", color: RED }}>
              We couldn't load your imported documents. Please try again.
            </div>
          )}

          {grouped.C3.length === 0 && grouped.C2.length === 0 && imported.linked.length === 0 && imported.unlinked.length === 0 && (
            <div data-testid="cc-m6-empty" className="text-center py-12 text-sm text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No documents on file for this voyage yet.
            </div>
          )}
        </div>
      )}
      {viewing && <DocumentViewer {...viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// Named exports for tests.
export const REVEAL_TIMEOUT_MS = DOCUMENT_ASSET_STORE_REVEAL_TIMEOUT_MS;
